import { config } from "./config";
import getUser from "./getUser";

/**
 * Wrapped fetch function [Fetch API](https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API)
 * This function wraps fetch and adds Authorization and X-Version headers expected  by our app's 
 * WebAPI. Callers can provide additional (or override) fetch arguments using requestInit parameter.
 * @param url URL to fetch
 * @param requestInit Additional (or override) underlying arguments to fetch
 * @param throwIfNot2xx (Default: true) When true, doFetch will throw the Response if it's not 2xx
 * @param returnIfNotSignedIn (Default: false) When true, doFetch will return immediately if the user is
 * not signed in. This is useful if we want to avoid a fetch in a loader nested under ProtectedLayout. This
 * ensures we don't get a wasteful fetch when ProtectedLayout determines the user is not signed in
 * @returns Response - If throwIfNot2xx is true, only 2xx Response's will be returned
 * @throws 500 Response if underlying fetch error (ex. Network Issue); Non 2xx Responses if throwIfNot2xx true
 */
export const doFetch = async (
  url: string,
  requestInit: RequestInit,
  throwIfNot2xx = true,
  returnIfNotSignedIn = false,
  params = {}
) => {
  const queryParams = new URLSearchParams(params);
  const user = getUser();
  if (!user && returnIfNotSignedIn) return null;
  if (!config.apiUrl) throw new Error("apiUrl not configured");
  let response: Response;
  let contentType;
  try {
    response = await fetch(`${config.apiUrl}/${url}?${queryParams}`, {
      ...requestInit,
      headers: {
        ...requestInit?.headers,
        Authorization: `Bearer ${user?.access_token}`,
        "X-Version": config.version
      }
    });

    contentType = response.headers.get('Content-Type');
  } catch (e: any) {
    throw new Response("Server unreachable", { status: 500 });
  }
  if (throwIfNot2xx && (response.status < 200 || response.status > 299)) {
    throw response;
  }
  if (contentType && contentType === 'text/html') {
    const html = await response.text();
    return html;
  }
  if (contentType && contentType === 'text/plain') {
    // Handle attachment response
    return response.blob()
  } else {
    return response.json();
  }
}